import { useEffect, useRef, useState } from 'react';

import { keys } from './keys';
import { TurnstileContainer } from './turnstileStyles';

// Singleton loader for the Turnstile script
let turnstileScriptPromise;
function loadTurnstileScript() {
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      if (window.turnstile) return resolve();
      const script = document.createElement('script');
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error('Turnstile script failed to load'));
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

/* 
    A[Component Mounts] --> B[Create Script Tag]
    B --> C[Load Turnstile API]
    C --> D[Render Widget]
    D --> E[Return Widget ID]
    E --> F[Call onWidgetId]
    A --> G[Component Unmounts]
    G --> H[Remove Script]
    H --> I[Remove Widget] 
*/

/**
 * A React component wrapping Cloudflare Turnstile without external dependencies.
 *
 * Props:
 * - onVerify(token: string): called when Turnstile issues a valid token
 * - onError?(): called on widget error
 * - onExpire?(): called when the token expires
 * - onWidgetId?(id: number, reset: () => void): called with the widget ID and a reset function
 * - theme?: 'light' | 'dark'
 * - className?: string
 */
export const Turnstile = ({
  onVerify,
  onError,
  onExpire,
  onWidgetId,
  theme = 'light',
  className,
  ...rest
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  let isMounted = true;

  // Load script & render widget on mount
  useEffect(() => {
    loadTurnstileScript()
      .then(() => {
        if (!isMounted || !window.turnstile || !containerRef.current) return;
        // Render the widget
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: keys.siteKey,
          theme,
          callback: onVerify,
          'error-callback': onError,
          'expired-callback': onExpire,
        });
        // Provide widget ID & reset function
        if (onWidgetId) {
          onWidgetId(widgetIdRef.current, () =>
            window.turnstile.reset(widgetIdRef.current),
          );
        }
      })
      .catch(err => console.error('Turnstile load error:', err));

    return () => {
      isMounted = false;
      // Clean up the widget instance
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, []);

  return (
    <>
      <TurnstileContainer ref={containerRef} className={className} {...rest} />
      <noscript>
        <iframe
          title="Turnstile fallback"
          src={`https://challenges.cloudflare.com/turnstile/v0/fallback?k=${keys.siteKey}`}
          sandbox="allow-scripts allow-same-origin"
          style={{ width: '100%', height: '65px', border: 'none' }}
        />
      </noscript>
    </>
  );
};
